/**
 * AI 力认证证书 SVG 编辑器
 * 简化版本，只保留核心功能
 */

import { checkStringContainChineseOrJapanese } from '@umu/umu-util';

/**
 * SVG 编辑器配置选项
 */
export interface ISvgEditorOptions {
    /**
     * 字体族名称，默认为 'Caet Noto Serif'
     */
    fontFamily?: string;
    /**
     * 自定义路径列表
     */
    customPath?: string[];
}

const defaultBuiltInPathList: string[] = [
    'userName',
    'title',
    'subTitle',
    'qr',
    'link',
    'theme',
    'date',
    'certificateId',
    'organization',
    'antiCounterfeitingTop',
    'antiCounterfeitingLeft',
    'antiCounterfeitingRight',
    'antiCounterfeitingBottom',
    'antiCounterfeitingCenter',
];

class SvgEditor {
    private svg: string;
    private svgElement: SVGSVGElement | null = null;
    private pathMap: Map<string, string>;
    private nodeMap: Map<string, SVGElement>;
    private fontFamily: string;

    constructor(svg: string, options?: ISvgEditorOptions) {
        if (!svg || typeof svg !== 'string') {
            throw new Error('SvgEditor: svg parameter is required and must be a string');
        }

        this.svg = svg;

        // 处理配置选项
        const config = options || {};
        this.fontFamily = config?.fontFamily || 'Caet Noto Serif';

        this.pathMap = new Map();
        const tmpList = [...defaultBuiltInPathList, ...(config.customPath || [])];
        tmpList.forEach((item: string) => {
            this.pathMap.set(item, `\${${item}}`);
        });
        this.nodeMap = new Map();

        this.parseSVG();
    }

    /**
     * 解析 SVG 字符串为 DOM 元素
     */
    private parseSVG(): void {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(this.svg, 'image/svg+xml');

            // 检查解析错误
            const parserError = doc.querySelector('parsererror');
            if (parserError) {
                throw new Error('Invalid SVG format: ' + parserError.textContent);
            }

            this.svgElement = doc.documentElement as any;

            if (!this.svgElement) {
                throw new Error('No SVG element found');
            }

            if (this.svgElement.tagName !== 'svg') {
                throw new Error('Invalid SVG content: root element is not SVG');
            }

            this.buildNodeMap();
            this.injectFontStyles();
        } catch (error) {
            console.error('Failed to initialize SVG editor:', error);
            // 提供降级方案
            this.createFallbackSVG();
        }
    }

    /**
     * 创建降级 SVG
     */
    private createFallbackSVG(): void {
        const fallbackSVG = `
            <svg width="800" height="600" xmlns="http://www.w3.org/2000/svg">
                <rect width="800" height="600" fill="#f8f9fa"/>
                <text x="400" y="300" text-anchor="middle" font-size="24" fill="#666">
                    SVG load failed, please check the file format
                </text>
            </svg>
        `;

        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(fallbackSVG, 'image/svg+xml');
            this.svgElement = doc.documentElement as any;
        } catch (error) {
            console.error('Failed to create fallback SVG:', error);
        }
    }

    /**
     * 注入字体样式
     */
    private injectFontStyles(): void {
        if (!this.svgElement) {
            return;
        }

        if (!this.fontFamily || typeof this.fontFamily !== 'string') {
            return;
        }

        try {
            // 查找或创建 defs 元素
            let defsElement = this.svgElement.querySelector('defs');
            if (!defsElement) {
                defsElement = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
                // 将 defs 插入到 svg 的第一个子元素位置
                if (this.svgElement.firstChild) {
                    this.svgElement.insertBefore(defsElement, this.svgElement.firstChild);
                } else {
                    this.svgElement.appendChild(defsElement);
                }
            }

            // 检查是否已存在字体样式
            const existingStyle = defsElement.querySelector('style[data-font-inject]');
            if (existingStyle) {
                // 如果已存在，更新字体
                existingStyle.textContent = `text { font-family: '${this.fontFamily}'; }`;
                return;
            }

            // 创建 style 元素
            const styleElement = document.createElementNS('http://www.w3.org/2000/svg', 'style');
            styleElement.setAttribute('data-font-inject', 'true');
            styleElement.textContent = `text { font-family: '${this.fontFamily}'; }`;

            // 将 style 元素插入到 defs 的第一个位置
            if (defsElement.firstChild) {
                defsElement.insertBefore(styleElement, defsElement.firstChild);
            } else {
                defsElement.appendChild(styleElement);
            }
        } catch (error) {
            console.error('SvgEditor: injectFontStyles run error:', error);
        }
    }

    /**
     * 构建节点映射
     */
    private buildNodeMap(): void {
        if (!this.svgElement) {
            return;
        }

        // 遍历所有占位符，查找对应的元素
        for (const [nodeKey, placeholder] of Array.from(this.pathMap.entries())) {
            // 查找包含此占位符的所有元素
            const elements = this.svgElement.querySelectorAll('*');
            let found = false;

            for (const element of Array.from(elements)) {
                // 检查文本内容
                if (element.textContent && element.textContent.includes(placeholder)) {
                    this.nodeMap.set(nodeKey, element as SVGElement);
                    found = true;
                    break;
                }

                // 检查属性
                for (const attr of Array.from(element.attributes)) {
                    if (attr.value.includes(placeholder)) {
                        this.nodeMap.set(nodeKey, element as SVGElement);
                        found = true;
                        break;
                    }
                }

                if (found) {
                    break;
                }
            }

            if (!found) {
                // 尝试直接搜索占位符文本
                this.svgElement.innerHTML.includes(placeholder);
            }
        }
    }

    /**
     * 替换节点内容
     */
    private replaceNodeContent(nodeKey: string, content: string): void {
        const element = this.nodeMap.get(nodeKey);
        if (!element) {
            return;
        }

        // 检查内容是否为空
        const isEmpty = !content || String(content).trim() === '';

        if (isEmpty) {
            // 如果内容为空，隐藏节点
            element.style.display = 'none';
            return;
        }

        // 如果内容不为空，显示节点并更新内容
        element.style.display = '';

        // 特殊处理链接
        if (nodeKey === 'link') {
            this.updateLinkElement(element, content);
        } else if (element.tagName === 'text' || element.tagName === 'tspan') {
            this.updateTextElement(element as SVGTextElement, content);
        } else if (element.tagName === 'image') {
            this.updateImageElement(element as SVGImageElement, content);
        } else {
            // 对于其他元素，尝试更新 textContent
            element.textContent = content;
        }
    }

    /**
     * 更新文本元素
     */
    private updateTextElement(element: SVGTextElement, content: string): void {
        // 检查是否有 data-auto-wrap 标志和 data-text-width 属性，如果有则支持自动换行
        const autoWrap = element.getAttribute('data-auto-wrap') === 'true';
        const textWidth = element.getAttribute('data-text-width');
        if (autoWrap && textWidth) {
            this.updateTextElementWithWrap(element, content, parseFloat(textWidth));
            return;
        }

        // 检查是否有 data-auto-fit 标志和 data-text-width 属性，如果有则支持单行自适应字体
        const autoFit = element.getAttribute('data-auto-fit') === 'true';
        if (autoFit && textWidth) {
            this.updateTextElementWithAutoFit(element, content, parseFloat(textWidth));
            return;
        }

        // 普通文本更新：先清空所有子元素（tspan），再设置文本
        this.clearTextChildren(element);
        element.textContent = content;
    }

    /**
     * 清空文本元素的所有子元素（tspan）
     */
    private clearTextChildren(element: SVGTextElement): void {
        while (element.firstChild) {
            element.removeChild(element.firstChild);
        }
    }

    /**
     * 更新文本元素（支持自动换行和自适应字体大小）
     */
    private updateTextElementWithWrap(element: SVGTextElement, content: string, maxWidth: number): void {
        if (!content || content.trim() === '') {
            element.style.display = 'none';
            return;
        }

        // 保存原始显示状态
        const originalDisplay = element.style.display;
        const originalVisibility = element.style.visibility;
        const originalOpacity = element.style.opacity;

        // 获取配置参数（从 data 属性获取，这些是模板中定义的原始值）
        const defaultFontSize = parseFloat(element.getAttribute('font-size') || '6');
        const minFontSize = parseFloat(element.getAttribute('data-min-font-size') || '4');
        // 不限制行数，让文本完整显示

        // 获取文本样式信息
        const textAnchor = element.getAttribute('text-anchor') || 'middle';
        // 始终使用模板中定义的原始中心位置，避免累积偏移
        const centerX = parseFloat(element.getAttribute('x') || '100.5');
        // 重要：始终使用模板原始 y 值（81），不从当前元素读取（可能已被修改）
        const originalCenterY = 81; // 模板中定义的区域中心 y 位置
        const fill = element.getAttribute('fill') || '';

        // 清空现有内容（包括所有 tspan），准备测量
        this.clearTextChildren(element);

        // 重置 y 位置到原始中心位置，避免累积偏移
        element.setAttribute('y', String(originalCenterY));

        // 临时显示元素以确保能正确测量
        // 如果元素是 display: none，getComputedTextLength() 可能返回 0
        element.style.display = '';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';

        // 1. 先用默认字体测量
        element.setAttribute('font-size', String(defaultFontSize));
        element.textContent = content;

        // 等待一帧确保字体已应用（对于动态加载的字体）
        let textWidth = 0;
        try {
            textWidth = element.getComputedTextLength();
            // 如果测量结果为 0，可能是字体未加载，使用估算
            if (textWidth === 0) {
                // 估算：中文字符约等于字体大小，英文字符约等于字体大小的 0.6 倍
                let estimatedWidth = 0;
                for (const char of content) {
                    const isCJK = checkStringContainChineseOrJapanese(char);
                    estimatedWidth += isCJK ? defaultFontSize : defaultFontSize * 0.6;
                }
                textWidth = estimatedWidth;
            }
        } catch (e) {
            console.warn('getComputedTextLength failed:', e);
            // 如果测量失败，使用估算
            let estimatedWidth = 0;
            for (const char of content) {
                const isCJK = checkStringContainChineseOrJapanese(char);
                estimatedWidth += isCJK ? defaultFontSize : defaultFontSize * 0.6;
            }
            textWidth = estimatedWidth;
        }

        let useSingleLine = false;
        let finalFontSize = defaultFontSize;

        if (textWidth > 0 && textWidth <= maxWidth) {
            // 单行显示，font-size=6
            useSingleLine = true;
            finalFontSize = defaultFontSize;
        } else if (textWidth > maxWidth) {
            // 2. 计算自适应字体大小
            const calculatedFontSize = (maxWidth / textWidth) * defaultFontSize;
            finalFontSize = Math.max(minFontSize, calculatedFontSize);

            // 3. 用新字体重新测量
            element.setAttribute('font-size', String(finalFontSize));
            element.textContent = content;
            try {
                textWidth = element.getComputedTextLength();
                if (textWidth === 0) {
                    // 估算
                    let estimatedWidth = 0;
                    for (const char of content) {
                        const isCJK = checkStringContainChineseOrJapanese(char);
                        estimatedWidth += isCJK ? finalFontSize : finalFontSize * 0.6;
                    }
                    textWidth = estimatedWidth;
                }
            } catch (e) {
                console.warn('getComputedTextLength failed on second measurement:', e);
                // 估算
                let estimatedWidth = 0;
                for (const char of content) {
                    const isCJK = checkStringContainChineseOrJapanese(char);
                    estimatedWidth += isCJK ? finalFontSize : finalFontSize * 0.6;
                }
                textWidth = estimatedWidth;
            }

            if (textWidth > 0 && textWidth <= maxWidth && finalFontSize >= minFontSize) {
                // 单行显示，动态字体
                useSingleLine = true;
            } else {
                // 多行换行显示，先使用最小字体进行换行测试
                useSingleLine = false;
                finalFontSize = minFontSize; // 先使用最小字体

                // 测试换行后需要多少行
                element.setAttribute('font-size', String(finalFontSize));
                element.textContent = content;
                const testLines = this.wrapText(content, maxWidth, finalFontSize, element);

                // 如果超过2行，进一步缩小字体到3
                if (testLines.length > 2) {
                    finalFontSize = 3; // 3行或更多时使用字体大小3
                }
            }
        }

        // 恢复原始显示状态
        element.style.display = originalDisplay;
        element.style.visibility = originalVisibility;
        element.style.opacity = originalOpacity;

        // 再次清空内容（测量时可能添加了临时内容）
        this.clearTextChildren(element);

        // 渲染文本
        if (useSingleLine) {
            // 单行显示：直接设置文本内容，调整 y 位置垂直居中
            element.setAttribute('font-size', String(finalFontSize));
            element.textContent = content;

            // 垂直居中：文本视觉中心到基线的距离约为 fontSize * 0.3
            const verticalOffset = finalFontSize * 0.3;
            element.setAttribute('y', String(originalCenterY + verticalOffset));
        } else {
            // 多行换行显示
            // 1. 先进行换行处理（需要元素处于可测量状态）
            element.style.display = '';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.setAttribute('font-size', String(finalFontSize));
            element.setAttribute('y', String(originalCenterY));

            const lines = this.wrapText(content, maxWidth, finalFontSize, element);
            const displayLines = lines; // 不截断，显示所有行

            // 恢复原始显示状态
            element.style.display = originalDisplay;
            element.style.visibility = originalVisibility;
            element.style.opacity = originalOpacity;

            // 再次清空内容（测量时可能添加了临时内容）
            this.clearTextChildren(element);

            // 2. 计算总高度和垂直偏移
            const lineHeight = finalFontSize * 1.2;
            const totalHeight = finalFontSize + (displayLines.length - 1) * lineHeight;
            // 多行文本垂直居中计算：
            // SVG 文本的 y 是基线位置，文本视觉中心应该在区域中心
            // 根据实际测试调整系数：
            // - 2行（font-size=4）时，y=80，offset=-1，coefficient=1.35
            // - 3行（font-size=3）时，y=77.4，offset=-3.6
            //   计算：-3.6 = totalHeight/2 - 3*coefficient
            //   totalHeight = 3 + 2*3.6 = 10.2
            //   -3.6 = 5.1 - 3*coefficient => coefficient = (5.1+3.6)/3 = 2.9
            // 使用动态系数：根据行数和字体大小调整
            let coefficient = 1.35; // 默认系数（2行，font-size=4）
            if (displayLines.length >= 3 && finalFontSize === 3) {
                // 3行（font-size=3）时，y=78.4，区域中心是81
                // offset = 78.4 - 81 = -2.6
                // totalHeight = 3 + 2*3.6 = 10.2
                // -2.6 = 10.2 / 2 - 3 * coefficient
                // -2.6 = 5.1 - 3 * coefficient
                // 3 * coefficient = 5.1 + 2.6 = 7.7
                // coefficient = 2.57
                coefficient = 2.57; // 3行或更多，font-size=3
            }
            const offset = totalHeight / 2 - finalFontSize * coefficient;
            const startY = originalCenterY + offset;

            // 3. 创建 tspan 元素
            displayLines.forEach((line, index) => {
                const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
                tspan.setAttribute('x', String(centerX));
                tspan.setAttribute('dy', index === 0 ? '0' : String(lineHeight));
                tspan.setAttribute('text-anchor', textAnchor);
                if (fill) {
                    tspan.setAttribute('fill', fill);
                }
                tspan.textContent = line;
                element.appendChild(tspan);
            });

            // 4. 设置父元素的 y 位置和字体大小
            element.setAttribute('y', String(startY));
            element.setAttribute('font-size', String(finalFontSize));
        }
    }

    /**
     * 更新文本元素（支持单行自适应字体大小，不换行）
     */
    private updateTextElementWithAutoFit(element: SVGTextElement, content: string, maxWidth: number): void {
        if (!content || (content && content.trim() === '')) {
            element.style.display = 'none';
            return;
        }

        // 保存原始显示状态
        const originalDisplay = element.style.display;
        const originalVisibility = element.style.visibility;
        const originalOpacity = element.style.opacity;

        // 获取配置参数
        // 重要：不能从 element.getAttribute('font-size') 获取默认字体，因为可能已经被修改为小字体
        // 需要根据元素类型确定默认字体大小
        // 检查是否是 userName（通过 y 位置或 data 属性判断）
        const isUserName =
            element.getAttribute('y') === '48.4' ||
            element.textContent?.includes('${userName}') ||
            element.getAttribute('data-text-width') === '105';
        const isSubTitle =
            element.getAttribute('y') === '67.6' ||
            element.textContent?.includes('${subTitle}') ||
            element.getAttribute('data-text-width') === '92.72';

        // 根据元素类型设置默认字体大小
        const defaultFontSize = isUserName
            ? 8.6
            : isSubTitle
            ? 4.8
            : parseFloat(element.getAttribute('font-size') || '4.8');
        const minFontSize = parseFloat(element.getAttribute('data-min-font-size') || (isUserName ? '4' : '3'));
        const fontSizeStep = parseFloat(element.getAttribute('data-font-size-step') || (isUserName ? '0.4' : '0.2'));

        // 重要：始终使用模板原始 y 值，不从当前元素读取（可能已被修改导致累积偏移）
        // 根据元素类型确定原始 y 位置
        const originalY = isUserName ? 48.4 : isSubTitle ? 67.6 : parseFloat(element.getAttribute('y') || '48.4');

        // 清空现有内容
        this.clearTextChildren(element);

        // 重置 y 位置到原始位置，避免累积偏移
        element.setAttribute('y', String(originalY));

        // 重要：重置字体大小到默认值，避免之前设置的小字体影响测量
        element.setAttribute('font-size', String(defaultFontSize));

        // 临时显示元素以确保能正确测量
        element.style.display = '';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';

        // 1. 先用默认字体测量（确保使用默认字体）
        element.setAttribute('font-size', String(defaultFontSize));
        element.textContent = content;

        let textWidth = 0;
        try {
            textWidth = element.getComputedTextLength();
            // 如果测量结果为 0，使用估算
            if (textWidth === 0) {
                let estimatedWidth = 0;
                for (const char of content) {
                    const isCJK = checkStringContainChineseOrJapanese(char);
                    estimatedWidth += isCJK ? defaultFontSize : defaultFontSize * 0.6;
                }
                textWidth = estimatedWidth;
            }
        } catch (e) {
            console.warn('getComputedTextLength failed:', e);
            // 估算
            let estimatedWidth = 0;
            for (const char of content) {
                const isCJK = checkStringContainChineseOrJapanese(char);
                estimatedWidth += isCJK ? defaultFontSize : defaultFontSize * 0.6;
            }
            textWidth = estimatedWidth;
        }

        let finalFontSize = defaultFontSize;
        let useMultiLine = false;

        // 2. 如果超出宽度，先尝试按配置的梯度递减字体大小
        if (textWidth > maxWidth) {
            // 字体梯度：从配置中获取（userName 为 0.4，subTitle 为 0.2）
            let testFontSize = defaultFontSize;
            let foundFit = false;

            // 从最大字体开始，逐步减小，找到能放下的最大字体
            while (testFontSize >= minFontSize && !foundFit) {
                element.setAttribute('font-size', String(testFontSize));
                element.textContent = content;

                let currentWidth = 0;
                try {
                    currentWidth = element.getComputedTextLength();
                    if (currentWidth === 0) {
                        // 估算
                        let estimatedWidth = 0;
                        for (const char of content) {
                            const isCJK = checkStringContainChineseOrJapanese(char);
                            estimatedWidth += isCJK ? testFontSize : testFontSize * 0.6;
                        }
                        currentWidth = estimatedWidth;
                    }
                } catch (e) {
                    console.warn('getComputedTextLength failed:', e);
                    // 估算
                    let estimatedWidth = 0;
                    for (const char of content) {
                        const isCJK = checkStringContainChineseOrJapanese(char);
                        estimatedWidth += isCJK ? testFontSize : testFontSize * 0.6;
                    }
                    currentWidth = estimatedWidth;
                }

                if (currentWidth <= maxWidth) {
                    // 找到了能放下的字体大小
                    finalFontSize = testFontSize;
                    foundFit = true;
                } else {
                    // 继续减小字体（使用配置的梯度）
                    testFontSize = Math.max(minFontSize, testFontSize - fontSizeStep);
                    // 如果已经是最小字体，停止循环
                    if (testFontSize <= minFontSize) {
                        finalFontSize = minFontSize;
                        break;
                    }
                }
            }

            // 如果即使使用最小字体也放不下，则使用换行
            if (!foundFit) {
                // 用最小字体测试是否仍然超出
                element.setAttribute('font-size', String(minFontSize));
                element.textContent = content;
                let minFontWidth = 0;
                try {
                    minFontWidth = element.getComputedTextLength();
                    if (minFontWidth === 0) {
                        let estimatedWidth = 0;
                        for (const char of content) {
                            const isCJK = checkStringContainChineseOrJapanese(char);
                            estimatedWidth += isCJK ? minFontSize : minFontSize * 0.6;
                        }
                        minFontWidth = estimatedWidth;
                    }
                } catch (e) {
                    let estimatedWidth = 0;
                    for (const char of content) {
                        const isCJK = checkStringContainChineseOrJapanese(char);
                        estimatedWidth += isCJK ? minFontSize : minFontSize * 0.6;
                    }
                    minFontWidth = estimatedWidth;
                }

                if (minFontWidth > maxWidth) {
                    // 即使最小字体也放不下，使用换行
                    useMultiLine = true;
                    finalFontSize = minFontSize;

                    // 对于 userName，如果超过2行，使用字体 3.5
                    if (isUserName) {
                        element.setAttribute('font-size', String(minFontSize));
                        element.textContent = content;
                        const testLines = this.wrapText(content, maxWidth, minFontSize, element);
                        if (testLines.length > 2) {
                            finalFontSize = 3.5; // userName 3行时使用字体 3.5
                        }
                    }
                } else {
                    // 最小字体可以放下
                    finalFontSize = minFontSize;
                }
            }
        }

        // 渲染文本
        if (useMultiLine) {
            // 多行换行显示
            // 1. 先进行换行处理（需要元素处于可测量状态）
            // 重要：重置 y 位置到原始值，避免累积偏移
            element.setAttribute('y', String(originalY));
            element.style.display = '';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.setAttribute('font-size', String(finalFontSize));

            const lines = this.wrapText(content, maxWidth, finalFontSize, element);

            // 恢复原始显示状态
            element.style.display = originalDisplay;
            element.style.visibility = originalVisibility;
            element.style.opacity = originalOpacity;

            // 再次清空内容
            this.clearTextChildren(element);

            // 2. 计算总高度和垂直偏移（垂直居中）
            const lineHeight = finalFontSize * 1.2;
            const totalHeight = finalFontSize + (lines.length - 1) * lineHeight;
            // 垂直居中：根据元素类型、行数和字体大小调整系数
            let coefficient = 1.35; // 默认系数

            if (isSubTitle) {
                // subTitle 垂直居中：根据实际测试调整系数
                // 2行（font-size=3）时，y=65，区域中心是67.6
                if (lines.length === 2 && finalFontSize === 3) {
                    coefficient = 1.97; // 2行，font-size=3
                } else if (lines.length >= 3 && finalFontSize === 3) {
                    coefficient = 2.57; // 3行或更多，font-size=3
                }
            } else if (isUserName) {
                // userName 垂直居中：根据实际测试调整系数
                // 2行（font-size=4）时，y=45，区域中心是48.4
                // offset = 45 - 48.4 = -3.4
                // totalHeight = 4 + 4.8 = 8.8
                // -3.4 = 8.8 / 2 - 4 * coefficient
                // -3.4 = 4.4 - 4 * coefficient
                // 4 * coefficient = 7.8
                // coefficient = 1.95
                // 3行（font-size=3.5）时，y=43，区域中心是48.4
                // offset = 43 - 48.4 = -5.4
                // totalHeight = 3.5 + 2*4.2 = 11.9
                // -5.4 = 11.9 / 2 - 3.5 * coefficient
                // -5.4 = 5.95 - 3.5 * coefficient
                // 3.5 * coefficient = 11.35
                // coefficient = 3.24
                if (lines.length === 2 && finalFontSize === 4) {
                    coefficient = 1.95; // 2行，font-size=4
                } else if (lines.length >= 3 && finalFontSize === 3.5) {
                    coefficient = 3.24; // 3行或更多，font-size=3.5
                } else {
                    coefficient = 1.35; // 默认
                }
            }
            const offset = totalHeight / 2 - finalFontSize * coefficient;
            const startY = originalY + offset;

            // 3. 创建 tspan 元素
            const textAnchor = element.getAttribute('text-anchor') || 'middle';
            const centerX = parseFloat(element.getAttribute('x') || '100');
            const fill = element.getAttribute('fill') || '';

            lines.forEach((line, index) => {
                const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
                tspan.setAttribute('x', String(centerX));
                tspan.setAttribute('dy', index === 0 ? '0' : String(lineHeight));
                tspan.setAttribute('text-anchor', textAnchor);
                if (fill) {
                    tspan.setAttribute('fill', fill);
                }
                tspan.textContent = line;
                element.appendChild(tspan);
            });

            // 4. 设置父元素的 y 位置和字体大小
            element.setAttribute('y', String(startY));
            element.setAttribute('font-size', String(finalFontSize));
        } else {
            // 单行显示，自适应字体大小
            // 恢复原始显示状态
            element.style.display = originalDisplay;
            element.style.visibility = originalVisibility;
            element.style.opacity = originalOpacity;

            // 再次清空内容（清除可能存在的 tspan 元素）
            this.clearTextChildren(element);

            // 渲染文本：单行显示，自适应字体大小
            // 重要：确保重置到原始 y 位置，避免之前多行时的偏移影响
            element.setAttribute('y', String(originalY)); // 先重置 y 位置
            element.setAttribute('font-size', String(finalFontSize));
            element.textContent = content; // 单行直接设置 textContent，不使用 tspan
        }
    }

    /**
     * 根据宽度自动换行文本
     */
    private wrapText(text: string, maxWidth: number, fontSize: number, tempElement: SVGTextElement): string[] {
        if (!text) {
            return [];
        }

        // 先测试测量是否可用
        tempElement.textContent = '测';
        let testWidth = 0;
        try {
            testWidth = tempElement.getComputedTextLength();
        } catch (e) {
            console.warn('getComputedTextLength failed, using fallback method');
        }

        // 如果测量失败或返回 0，使用字符数估算
        if (testWidth === 0 || isNaN(testWidth)) {
            console.warn('Text measurement not available, using character-based wrapping');
            return this.wrapTextByCharacterCount(text, maxWidth, fontSize);
        }

        const lines: string[] = [];

        // 处理中文字符和英文单词的混合文本
        // 中文字符按字符分割，英文按单词分割
        const tokens: string[] = [];
        let currentToken = '';

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            // 判断是否为中文字符（包括中文标点）
            const isCJK = checkStringContainChineseOrJapanese(char);

            if (isCJK) {
                // 中文字符：如果当前有英文token，先保存
                if (currentToken) {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                tokens.push(char);
            } else if (/\s/.test(char)) {
                // 空格：保存当前token并添加空格
                if (currentToken) {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                tokens.push(char);
            } else {
                // 英文字符：累积到当前token
                currentToken += char;
            }
        }

        // 添加最后一个token
        if (currentToken) {
            tokens.push(currentToken);
        }

        let currentLine = '';

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];

            // 先测量单个token的宽度
            tempElement.textContent = token;
            let tokenWidth = 0;
            try {
                tokenWidth = tempElement.getComputedTextLength();
                // 如果返回 0 或异常值，使用估算
                if (tokenWidth === 0 || isNaN(tokenWidth)) {
                    // 中文字符约等于字体大小，英文字符约等于字体大小的 0.6 倍
                    const isCJK = checkStringContainChineseOrJapanese(token);
                    tokenWidth = isCJK ? token.length * fontSize : token.length * fontSize * 0.6;
                }
            } catch (e) {
                // 如果测量失败，使用估算值
                const isCJK = checkStringContainChineseOrJapanese(token);
                tokenWidth = isCJK ? token.length * fontSize : token.length * fontSize * 0.6;
            }

            // 调试信息
            if (process.env.NODE_ENV === 'development' && i < 5) {
                console.log('Token measurement:', {
                    token,
                    tokenWidth,
                    maxWidth,
                    ratio: tokenWidth / maxWidth,
                });
            }

            // 如果单个token就超过宽度，强制换行（避免无限循环）
            if (tokenWidth > maxWidth) {
                // 如果当前行有内容，先保存当前行
                if (currentLine.trim()) {
                    lines.push(currentLine.trim());
                    currentLine = '';
                }
                // 强制添加超长的token到新行（即使是单个字符）
                lines.push(token);
                continue;
            }

            // 测量添加token后的总宽度
            const testLine = currentLine + token;
            tempElement.textContent = testLine;
            let textWidth = 0;
            try {
                textWidth = tempElement.getComputedTextLength();
                // 如果返回 0 或异常值，使用估算
                if (textWidth === 0 || isNaN(textWidth)) {
                    // 估算：中文字符约等于字体大小，英文字符约等于字体大小的 0.6 倍
                    let estimatedWidth = 0;
                    for (const char of testLine) {
                        const isCJK = checkStringContainChineseOrJapanese(char);
                        estimatedWidth += isCJK ? fontSize : fontSize * 0.6;
                    }
                    textWidth = estimatedWidth;
                }
            } catch (e) {
                // 如果测量失败，使用估算值
                let estimatedWidth = 0;
                for (const char of testLine) {
                    const isCJK = checkStringContainChineseOrJapanese(char);
                    estimatedWidth += isCJK ? fontSize : fontSize * 0.6;
                }
                textWidth = estimatedWidth;
            }

            // 调试信息
            if (process.env.NODE_ENV === 'development' && (i < 5 || textWidth > maxWidth)) {
                console.log('Line measurement:', {
                    token,
                    testLine,
                    textWidth,
                    maxWidth,
                    currentLine: currentLine.trim(),
                    shouldWrap: textWidth > maxWidth && currentLine.trim() !== '',
                });
            }

            if (textWidth > maxWidth && currentLine.trim() !== '') {
                // 当前行已满，保存当前行，开始新行
                lines.push(currentLine.trim());
                currentLine = token;
            } else {
                // 可以添加到当前行
                currentLine = testLine;
            }
        }

        // 添加最后一行
        if (currentLine.trim()) {
            lines.push(currentLine.trim());
        }

        return lines.length > 0 ? lines : [text];
    }

    /**
     * 根据字符数估算换行（备用方案，当测量不可用时使用）
     */
    private wrapTextByCharacterCount(text: string, maxWidth: number, fontSize: number): string[] {
        if (!text) {
            return [];
        }

        // 估算每行可容纳的字符数
        // 中文字符约等于字体大小，英文字符约等于字体大小的 0.6 倍
        // 估算：maxWidth / fontSize 约为中文字符数
        const avgCharWidth = fontSize; // 使用中文字符宽度作为平均值
        const charsPerLine = Math.floor(maxWidth / avgCharWidth);

        if (charsPerLine <= 0) {
            return [text];
        }

        const lines: string[] = [];
        let currentLine = '';

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const testLine = currentLine + char;

            // 估算当前行的宽度
            let estimatedWidth = 0;
            for (const c of testLine) {
                const isCJK = checkStringContainChineseOrJapanese(c);
                estimatedWidth += isCJK ? fontSize : fontSize * 0.6;
            }

            if (estimatedWidth > maxWidth && currentLine.trim() !== '') {
                // 当前行已满，保存当前行，开始新行
                lines.push(currentLine.trim());
                currentLine = char;
            } else {
                currentLine = testLine;
            }
        }

        // 添加最后一行
        if (currentLine.trim()) {
            lines.push(currentLine.trim());
        }

        return lines.length > 0 ? lines : [text];
    }

    /**
     * 更新图片元素
     */
    private updateImageElement(element: SVGImageElement, content: string): void {
        if (this.isUrl(content)) {
            // 如果是 URL，生成二维码
            const qrCodeDataUrl = this.generateQRCode(content, element);
            element.setAttribute('href', qrCodeDataUrl);
        } else {
            // 直接使用内容作为图片源
            element.setAttribute('href', content);
        }
    }

    /**
     * 更新链接元素
     */
    private updateLinkElement(element: SVGElement, content: string): void {
        // 设置文本内容
        element.textContent = content;

        // 设置字体颜色为 #1e6ee6
        element.setAttribute('fill', '#1e6ee6');

        // 设置样式
        element.setAttribute('style', 'cursor: pointer; text-decoration: underline;');

        // 设置数据属性（用于事件委托）
        element.setAttribute('data-link', content);
        element.setAttribute('data-link-type', 'verification');
    }

    /**
     * 检查是否为 URL
     */
    private isUrl(str: string): boolean {
        try {
            new URL(str);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * 生成二维码
     */
    private generateQRCode(url: string, targetElement: SVGImageElement): string {
        try {
            // 获取目标元素的尺寸
            let width = this.getElementWidth(targetElement);
            let height = this.getElementHeight(targetElement);
            // 由于是svg 坐标系，可能导致width和height 很小，因此给需要给一个最小值
            width = Math.max(width, 256);
            height = Math.max(height, 256);
            // 使用全局 QRCode 库
            if ((window as any)?.QRCode) {
                return this.createQRCodeDataUrl(url, width, height);
            } else {
                // 降级方案：创建简单的二维码
                return this.createFallbackQRCode(url, width, height);
            }
        } catch (error) {
            console.error('Failed to generate QR code:', error);
            return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjZjBmMGYwIi8+PC9zdmc+';
        }
    }

    /**
     * 创建二维码数据 URL
     */
    private createQRCodeDataUrl(text: string, width: number, height: number): string {
        try {
            // 创建临时容器
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            document.body.appendChild(tempContainer);

            // 使用 QRCode 库生成二维码
            new (window as any).QRCode(tempContainer, {
                text: text,
                width: width,
                height: height,
                margin: 0,
                color: '#000000',
                errorCorrectionLevel: 'M',
            });

            // 获取生成的 canvas
            const canvas = tempContainer.querySelector('canvas');
            if (canvas) {
                const dataUrl = canvas.toDataURL('image/png');
                document.body.removeChild(tempContainer);
                return dataUrl;
            }

            document.body.removeChild(tempContainer);
            return this.createFallbackQRCode(text, width, height);
        } catch (error) {
            console.error('QRCode generation failed:', error);
            return this.createFallbackQRCode(text, width, height);
        }
    }

    /**
     * 创建降级二维码
     */
    private createFallbackQRCode(text: string, width: number, height: number): string {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;

        // 绘制简单的二维码图案
        this.drawSimpleQRCode(ctx, text, width, height);
        return canvas.toDataURL('image/png');
    }

    /**
     * 绘制简单二维码
     */
    private drawSimpleQRCode(ctx: CanvasRenderingContext2D, text: string, width: number, height: number): void {
        // 清空画布
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        // 生成简单的二维码图案
        const pattern = this.generateQRPattern(text, width, height);

        // 绘制图案
        ctx.fillStyle = '#000000';
        for (let y = 0; y < height; y += 4) {
            for (let x = 0; x < width; x += 4) {
                if (pattern[y / 4] && pattern[y / 4][x / 4]) {
                    ctx.fillRect(x, y, 3, 3);
                }
            }
        }
    }

    /**
     * 生成二维码图案
     */
    private generateQRPattern(text: string, width: number, height: number): boolean[][] {
        const pattern: boolean[][] = [];
        const hash = this.simpleHash(text);

        for (let y = 0; y < height / 4; y++) {
            pattern[y] = [];
            for (let x = 0; x < width / 4; x++) {
                const index = (y * (width / 4) + x + hash) % 100;
                pattern[y][x] = index % 3 === 0;
            }
        }

        return pattern;
    }

    /**
     * 简单哈希函数
     */
    private simpleHash(str: string): number {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // 转换为 32 位整数
        }
        return Math.abs(hash);
    }

    /**
     * 获取元素宽度
     */
    private getElementWidth(element: SVGImageElement): number {
        const width = element.getAttribute('width');
        return width ? parseInt(width) : 60;
    }

    /**
     * 获取元素高度
     */
    private getElementHeight(element: SVGImageElement): number {
        const height = element.getAttribute('height');
        return height ? parseInt(height) : 60;
    }

    // 公共方法
    public setUserName(name: string): void {
        this.replaceNodeContent('userName', name);
    }

    public setTitle(title: string): void {
        this.replaceNodeContent('title', title);
    }

    public setSubTitle(subTitle: string): void {
        this.replaceNodeContent('subTitle', subTitle);
    }

    public setQr(qr: string): void {
        this.replaceNodeContent('qr', qr);
    }

    public setLink(link: string): void {
        this.replaceNodeContent('link', link);
    }

    public setTheme(theme: string): void {
        this.replaceNodeContent('theme', theme);
    }

    public setDate(date: string): void {
        this.replaceNodeContent('date', date);
    }

    public setCertificateId(id: string): void {
        this.replaceNodeContent('certificateId', id);
    }

    public setOrganization(org: string): void {
        this.replaceNodeContent('organization', org);
    }

    /**
     * 通用方法：根据节点名称和内容进行替换 【默认节点已经在初始化的时候进行了传入】
     * @param nodeKey 节点键名
     * @param content 要填充的内容
     */
    public setNodeContent(nodeKey: string, content: string): void {
        if (!nodeKey || typeof nodeKey !== 'string') {
            return;
        }

        if (content === null || content === undefined) {
            return;
        }

        this.replaceNodeContent(nodeKey, content);
    }

    /**
     * 批量设置节点内容：根据对象配置批量替换内容
     * @param contentMap 内容映射对象，键为节点名称，值为要填充的内容
     */
    public setBatchContent(contentMap: Record<string, string>): void {
        if (!contentMap || typeof contentMap !== 'object') {
            return;
        }

        try {
            for (const [nodeKey, content] of Object.entries(contentMap)) {
                if (nodeKey && content !== null && content !== undefined) {
                    this.replaceNodeContent(nodeKey, content);
                }
            }
        } catch (error) {
            console.error('SvgEditor: setBatchContent run error:', error);
        }
    }

    /**
     * 防伪函数：将防伪标识拆解并转换为ASCII码
     * @param antiCounterfeitingId 防伪标识，长度必须大于6
     */
    public setAntiCounterfeiting(antiCounterfeitingId: string): void {
        if (!antiCounterfeitingId || typeof antiCounterfeitingId !== 'string') {
            return;
        }

        if (antiCounterfeitingId.length <= 6) {
            return;
        }

        try {
            // 将防伪标识拆解为5部分
            const parts = this.splitStringIntoFiveParts(antiCounterfeitingId);

            // 将每部分转换为ASCII码
            const asciiParts = parts.map((part) => this.convertToAscii(part));

            // 调用 setBatchContent 方法设置防伪内容
            this.setBatchContent({
                antiCounterfeitingTop: asciiParts[0],
                antiCounterfeitingLeft: asciiParts[1],
                antiCounterfeitingRight: asciiParts[2],
                antiCounterfeitingBottom: asciiParts[3],
                antiCounterfeitingCenter: asciiParts[4],
            });
        } catch (error) {
            console.error('SvgEditor: setAntiCounterfeiting :', error);
        }
    }

    /**
     * 将字符串拆解为5部分（尽可能均匀）
     * @param str 要拆解的字符串
     * @returns 拆解后的5个部分
     */
    private splitStringIntoFiveParts(str: string): string[] {
        const length = str.length;
        const parts: string[] = [];

        // 计算每部分的基础长度和余数
        const baseLength = Math.floor(length / 5);
        const remainder = length % 5;

        let startIndex = 0;

        for (let i = 0; i < 5; i++) {
            // 前 remainder 个部分多分配一个字符
            const currentLength = baseLength + (i < remainder ? 1 : 0);
            const endIndex = startIndex + currentLength;

            parts.push(str.substring(startIndex, endIndex));
            startIndex = endIndex;
        }

        return parts;
    }

    /**
     * 将字符串转换为ASCII码（用空格间隔）
     * @param str 要转换的字符串
     * @returns ASCII码字符串
     */
    private convertToAscii(str: string): string {
        if (!str) {
            return '';
        }

        const asciiCodes: number[] = [];

        for (let i = 0; i < str.length; i++) {
            asciiCodes.push(str.charCodeAt(i));
        }

        return asciiCodes.join(' ');
    }

    /**
     * 设置水印
     * @param watermarkConfig 水印配置
     */
    public setWatermark(watermarkConfig: {
        text?: string;
        color?: string;
        size?: number;
        trasparency?: number;
        rotate?: number;
        rows?: number;
        splitCount?: number;
    }): void {
        if (!this.svgElement || !watermarkConfig) {
            return;
        }

        // 使用默认值
        const text = watermarkConfig.text || 'UMU';
        if (!text) {
            return;
        }

        try {
            // 移除旧的水印
            this.removeWatermark();

            // 使用默认值
            const { color, size, trasparency, rotate, rows: configRows, splitCount } = watermarkConfig;

            // 获取 SVG 尺寸
            const svgWidth = this.getSvgWidth();
            const svgHeight = this.getSvgHeight();

            if (svgWidth <= 0 || svgHeight <= 0) {
                return;
            }

            // 创建水印组
            const watermarkGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            watermarkGroup.setAttribute('id', 'watermark-group');
            watermarkGroup.setAttribute('opacity', String(trasparency ?? 0.2));

            // 默认参数
            const fontSize = size || 18;
            const defaultColor = color || '#999';
            const rotateDegree = rotate || 30;

            // 使用配置的行数和间隔数（准确限制，支持 0 值）
            const rows = configRows !== undefined && configRows !== null ? configRows : 6;
            const splitCountValue = splitCount !== undefined && splitCount !== null ? splitCount : 4;

            // 如果行数为 0 或负数，不生成水印
            if (rows <= 0) {
                return;
            }

            // 计算每行的垂直间距（扩展到边界外，产生截断效果）
            // 垂直方向：从 -5% 到 105% 的范围，让上下边界有水印被截断
            const verticalStart = -svgHeight * 0.05;
            const verticalEnd = svgHeight * 1.05;
            const verticalRange = verticalEnd - verticalStart;
            const offsetY = verticalRange / (rows + 1);

            // 水平方向：从 -10% 到 110% 的范围，让左右边界有水印被截断
            // 向右偏移量（整体向右移动 SVG 宽度的 5%）
            const rightOffset = svgWidth * 0.05;
            const horizontalStart = -svgWidth * 0.1 + rightOffset;
            const horizontalEnd = svgWidth * 1.1 + rightOffset;
            const horizontalRange = horizontalEnd - horizontalStart;

            // 创建水印文本元素
            // rows 表示行数，splitCount 表示每行的水印数量
            for (let i = 0; i < rows; i++) {
                for (let j = 0; j < splitCountValue; j++) {
                    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                    // 计算水印位置（在扩展的范围内均匀分布，产生边界截断效果）
                    const x = horizontalStart + ((j + 1) * horizontalRange) / (splitCountValue + 1);
                    const y = verticalStart + (i + 1) * offsetY;
                    textElement.setAttribute('x', String(x));
                    textElement.setAttribute('y', String(y));
                    textElement.setAttribute('font-size', String(fontSize));
                    textElement.setAttribute('fill', defaultColor);
                    textElement.setAttribute('text-anchor', 'middle');
                    textElement.setAttribute('dominant-baseline', 'middle');
                    textElement.setAttribute('transform', `rotate(${-rotateDegree} ${x} ${y})`);
                    textElement.textContent = text;
                    watermarkGroup.appendChild(textElement);
                }
            }

            // 将水印组添加到 SVG 中（放在最上层）
            if (this.svgElement) {
                this.svgElement.appendChild(watermarkGroup);
            }
        } catch (error) {
            console.error('SvgEditor: setWatermark run error:', error);
        }
    }

    /**
     * 移除水印（私有方法，内部使用）
     */
    private removeWatermark(): void {
        if (!this.svgElement) {
            return;
        }

        try {
            const watermarkGroup = this.svgElement.querySelector('#watermark-group');
            if (watermarkGroup) {
                this.svgElement.removeChild(watermarkGroup);
            }
        } catch (error) {
            console.error('SvgEditor: removeWatermark run error:', error);
        }
    }

    /**
     * 清除水印（公共方法）
     */
    public clearWatermark(): void {
        this.removeWatermark();
    }

    /**
     * 获取 SVG 宽度
     */
    private getSvgWidth(): number {
        if (!this.svgElement) {
            return 0;
        }

        try {
            const width = this.svgElement.getAttribute('width');
            const viewBox = this.svgElement.getAttribute('viewBox');

            if (width) {
                const parsedWidth = parseFloat(width);
                if (!isNaN(parsedWidth) && parsedWidth > 0) {
                    return parsedWidth;
                }
            }

            if (viewBox) {
                const parts = viewBox.split(' ');
                if (parts.length >= 3) {
                    const parsedWidth = parseFloat(parts[2]);
                    if (!isNaN(parsedWidth) && parsedWidth > 0) {
                        return parsedWidth;
                    }
                }
            }

            // 默认宽度
            return 800;
        } catch (error) {
            console.error('SvgEditor: getSvgWidth run error:', error);
            return 800;
        }
    }

    /**
     * 获取 SVG 高度
     */
    private getSvgHeight(): number {
        if (!this.svgElement) {
            return 0;
        }

        try {
            const height = this.svgElement.getAttribute('height');
            const viewBox = this.svgElement.getAttribute('viewBox');

            if (height) {
                const parsedHeight = parseFloat(height);
                if (!isNaN(parsedHeight) && parsedHeight > 0) {
                    return parsedHeight;
                }
            }

            if (viewBox) {
                const parts = viewBox.split(' ');
                if (parts.length >= 4) {
                    const parsedHeight = parseFloat(parts[3]);
                    if (!isNaN(parsedHeight) && parsedHeight > 0) {
                        return parsedHeight;
                    }
                }
            }

            // 默认高度
            return 600;
        } catch (error) {
            console.error('SvgEditor: getSvgHeight run error:', error);
            return 600;
        }
    }

    public getSvg(): string {
        if (!this.svgElement) {
            return this.svg;
        }
        return new XMLSerializer().serializeToString(this.svgElement);
    }

    public reset(): void {
        this.parseSVG();
    }

    public destroy(): void {
        this.svgElement = null;
        this.nodeMap.clear();
    }
}

export default SvgEditor;
